import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft, RefreshCw, Download, Play, Pause, RotateCcw,
  CheckCircle, XCircle, Clock, Send, ChevronLeft, ChevronRight,
  Search, Mail, AlertTriangle, Trash2
} from 'lucide-react';
import {
  fetchCampaignDetail, fetchRecipients, startSending,
  pauseCampaign, retryCampaign, confirmCampaign, deleteCampaign,
  getRecipientsExportUrl
} from '../../services/campaignService';
import { getAccessToken } from '../../utils/auth';

const STATUS_BADGES = {
  draft:      { label: 'Draft',     bg: 'bg-gray-100',    text: 'text-gray-700' },
  test_sent:  { label: 'Test Sent', bg: 'bg-blue-100',    text: 'text-blue-700' },
  confirmed:  { label: 'Confirmed', bg: 'bg-yellow-100',  text: 'text-yellow-700' },
  sending:    { label: 'Sending',   bg: 'bg-indigo-100',  text: 'text-indigo-700' },
  paused:     { label: 'Paused',    bg: 'bg-orange-100',  text: 'text-orange-700' },
  completed:  { label: 'Completed', bg: 'bg-emerald-100', text: 'text-emerald-700' },
  failed:     { label: 'Failed',    bg: 'bg-red-100',     text: 'text-red-700' },
};

const RECIPIENT_STATUS = {
  queued:  { label: 'Queued',  icon: Clock,       color: 'text-gray-500' },
  sending: { label: 'Sending', icon: Send,        color: 'text-indigo-500' },
  sent:    { label: 'Sent',    icon: CheckCircle, color: 'text-emerald-500' },
  failed:  { label: 'Failed',  icon: XCircle,     color: 'text-red-500' },
  bounced: { label: 'Bounced', icon: AlertTriangle, color: 'text-orange-500' },
  skipped: { label: 'Skipped', icon: Clock,       color: 'text-gray-400' },
};

const TYPE_LABELS = {
  software_launch: 'Software Launch',
  onboarding:      'Onboarding',
  offboarding:     'Offboarding',
  new_features:    'New Features',
  custom:          'Custom',
};

const CampaignDetail = ({ campaignId, onBack }) => {
  const [campaign, setCampaign]       = useState(null);
  const [summary, setSummary]         = useState(null);
  const [testSends, setTestSends]     = useState([]);
  const [recipients, setRecipients]   = useState([]);
  const [pagination, setPagination]   = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });
  const [isLoading, setIsLoading]     = useState(true);
  const [error, setError]             = useState('');
  const [success, setSuccess]         = useState('');
  const [actionLoading, setActionLoading] = useState('');

  // Recipient filters
  const [recipientSearch, setRecipientSearch] = useState('');
  const [recipientStatus, setRecipientStatus] = useState('');

  // Tab
  const [activeTab, setActiveTab] = useState('overview'); // overview | recipients | preview

  const loadCampaign = useCallback(async () => {
    try {
      const data = await fetchCampaignDetail(campaignId);
      setCampaign(data.campaign);
      setSummary(data.summary);
      setTestSends(data.testSends || []);
    } catch (err) {
      setError(err.message || 'Failed to load campaign');
    } finally {
      setIsLoading(false);
    }
  }, [campaignId]);

  const loadRecipients = useCallback(async (page = 1) => {
    try {
      const data = await fetchRecipients(campaignId, {
        page,
        limit: pagination.limit,
        status: recipientStatus || undefined,
        search: recipientSearch || undefined,
      });
      setRecipients(data.recipients || []);
      setPagination(data.pagination || { page: 1, limit: 50, total: 0, totalPages: 0 });
    } catch (err) {
      setError(err.message || 'Failed to load recipients');
    }
  }, [campaignId, pagination.limit, recipientStatus, recipientSearch]);

  useEffect(() => {
    loadCampaign();
  }, [loadCampaign]);

  useEffect(() => {
    if (activeTab === 'recipients') {
      loadRecipients(1);
    }
  }, [activeTab, recipientStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh for sending campaigns
  useEffect(() => {
    if (campaign?.status === 'sending') {
      const interval = setInterval(() => {
        loadCampaign();
        if (activeTab === 'recipients') loadRecipients(pagination.page);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [campaign?.status, activeTab, pagination.page, loadCampaign, loadRecipients]);

  // Actions
  async function handleAction(action, actionName) {
    setActionLoading(actionName);
    setError('');
    setSuccess('');
    try {
      await action(campaignId);
      setSuccess(`Campaign ${actionName} successfully`);
      await loadCampaign();
    } catch (err) {
      setError(err.message || `Failed to ${actionName}`);
    } finally {
      setActionLoading('');
    }
  }

  async function handleDelete() {
    if (!window.confirm('Are you sure you want to delete this draft campaign?')) return;
    setActionLoading('delete');
    try {
      await deleteCampaign(campaignId);
      onBack();
    } catch (err) {
      setError(err.message || 'Failed to delete');
      setActionLoading('');
    }
  }

  // Export
  function handleExport(status) {
    const url = getRecipientsExportUrl(campaignId, status);
    const token = getAccessToken();
    // Open in new tab with auth header via fetch + blob download
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        if (!res.ok) throw new Error('Export failed');
        return res.blob();
      })
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `campaign_${campaignId}_recipients${status ? `_${status}` : ''}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(err => setError(err.message));
  }

  function handleRecipientSearch(e) {
    e.preventDefault();
    loadRecipients(1);
  }

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-8 flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-3 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-8">
        <p className="text-red-600">Campaign not found</p>
        <button onClick={onBack} className="mt-4 text-emerald-600 hover:underline text-sm">← Back</button>
      </div>
    );
  }

  const badge = STATUS_BADGES[campaign.status] || STATUS_BADGES.draft;
  const progress = campaign.total_recipients > 0
    ? Math.round(((campaign.sent_count + campaign.failed_count) / campaign.total_recipients) * 100)
    : 0;

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <div className="flex items-center space-x-3">
              <h1 className="text-xl font-bold text-gray-900">{campaign.subject}</h1>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
                {badge.label}
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-0.5">
              {TYPE_LABELS[campaign.type]} • Created by {campaign.created_by_name} • {new Date(campaign.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center space-x-2">
          <button
            onClick={() => { loadCampaign(); if (activeTab === 'recipients') loadRecipients(pagination.page); }}
            className="p-2 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>

          {campaign.status === 'confirmed' && (
            <button
              onClick={() => {
                // Offboarding deactivates every recipient as it sends, so this
                // one gets an explicit confirmation rather than a single click
                if (campaign.type === 'offboarding' && !window.confirm(
                  `This will REVOKE ACCESS for up to ${campaign.total_recipients} recipient(s) and then email them.\n\n`
                  + 'Superadmin accounts and your own account are never affected. '
                  + 'Access can be restored afterwards from User Management.\n\nContinue?'
                )) return;
                handleAction(startSending, 'started');
              }}
              disabled={!!actionLoading}
              className={`flex items-center space-x-1.5 px-3 py-2 text-white rounded-lg disabled:opacity-50 text-sm font-medium ${
                campaign.type === 'offboarding'
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-emerald-600 hover:bg-emerald-700'
              }`}
            >
              <Play className="w-3.5 h-3.5" />
              <span>
                {actionLoading === 'started'
                  ? 'Starting...'
                  : campaign.type === 'offboarding' ? 'Revoke Access & Send' : 'Start Sending'}
              </span>
            </button>
          )}

          {campaign.status === 'sending' && (
            <button
              onClick={() => handleAction(pauseCampaign, 'paused')}
              disabled={!!actionLoading}
              className="flex items-center space-x-1.5 px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 text-sm font-medium"
            >
              <Pause className="w-3.5 h-3.5" />
              <span>{actionLoading === 'paused' ? 'Pausing...' : 'Pause'}</span>
            </button>
          )}

          {campaign.status === 'paused' && (
            <button
              onClick={() => handleAction(startSending, 'resumed')}
              disabled={!!actionLoading}
              className="flex items-center space-x-1.5 px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 text-sm font-medium"
            >
              <Play className="w-3.5 h-3.5" />
              <span>Resume</span>
            </button>
          )}

          {['completed', 'paused', 'failed'].includes(campaign.status) && campaign.failed_count > 0 && (
            <button
              onClick={() => handleAction(retryCampaign, 'retried')}
              disabled={!!actionLoading}
              className="flex items-center space-x-1.5 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span>Retry Failed</span>
            </button>
          )}

          {['draft', 'test_sent'].includes(campaign.status) && (
            <>
              <button
                onClick={() => handleAction(confirmCampaign, 'confirmed')}
                disabled={!!actionLoading || campaign.total_recipients === 0}
                className="flex items-center space-x-1.5 px-3 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 text-sm font-medium"
              >
                <CheckCircle className="w-3.5 h-3.5" />
                <span>Confirm</span>
              </button>
              <button
                onClick={handleDelete}
                disabled={!!actionLoading}
                className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                title="Delete Draft"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}
      {success && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-emerald-700">{success}</p>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <StatCard label="Total" value={campaign.total_recipients} color="text-gray-900" />
        <StatCard label="Sent" value={summary?.sent || 0} color="text-emerald-600" />
        <StatCard label="Failed" value={summary?.failed || 0} color="text-red-600" />
        <StatCard label="Queued" value={summary?.queued || 0} color="text-blue-600" />
        <StatCard label="Progress" value={`${progress}%`} color="text-indigo-600" />
      </div>

      {/* Progress Bar */}
      {campaign.status === 'sending' && (
        <div className="mb-6">
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className="bg-emerald-500 h-2.5 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Sending at {campaign.send_rate} emails/minute...
          </p>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <div className="flex space-x-6">
          {['overview', 'recipients', 'preview'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors capitalize ${
                activeTab === tab
                  ? 'border-emerald-600 text-emerald-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Campaign Info */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Campaign Details</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <InfoItem label="Type" value={TYPE_LABELS[campaign.type]} />
              <InfoItem label="Status" value={badge.label} />
              <InfoItem label="Created" value={formatDateTime(campaign.created_at)} />
              <InfoItem label="Started" value={campaign.sent_at ? formatDateTime(campaign.sent_at) : '—'} />
              <InfoItem label="Completed" value={campaign.completed_at ? formatDateTime(campaign.completed_at) : '—'} />
            </div>
          </div>

          {/* Test Sends */}
          {testSends.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Test Sends</h3>
              <div className="space-y-2">
                {testSends.map(ts => (
                  <div key={ts.id} className="flex items-center justify-between text-sm py-2 border-b border-gray-100 last:border-0">
                    <div>
                      <span className="text-gray-700">{ts.test_email}</span>
                      <span className="text-gray-400 ml-2">by {ts.sent_by_name}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`text-xs ${ts.status === 'sent' ? 'text-emerald-600' : 'text-red-600'}`}>
                        {ts.status}
                      </span>
                      <span className="text-xs text-gray-400">
                        {formatDateTime(ts.created_at)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      )}

      {activeTab === 'recipients' && (
        <div>
          {/* Filters + Export */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-3">
              <form onSubmit={handleRecipientSearch} className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={recipientSearch}
                  onChange={e => setRecipientSearch(e.target.value)}
                  placeholder="Search email/name..."
                  className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </form>
              <select
                value={recipientStatus}
                onChange={e => setRecipientStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="">All Statuses</option>
                <option value="queued">Queued</option>
                <option value="sent">Sent</option>
                <option value="failed">Failed</option>
              </select>
            </div>

            {/* Export Buttons */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleExport('')}
                className="flex items-center space-x-1.5 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium"
              >
                <Download className="w-3.5 h-3.5" />
                <span>Export All</span>
              </button>
              {(summary?.failed > 0 || summary?.bounced > 0) && (
                <button
                  onClick={() => handleExport('failed')}
                  className="flex items-center space-x-1.5 px-3 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 text-sm font-medium"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span>Export Failed</span>
                </button>
              )}
            </div>
          </div>

          {/* Recipients Table */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Role</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Attempts</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Sent At</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Error</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {recipients.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">
                        No recipients found
                      </td>
                    </tr>
                  ) : (
                    recipients.map(r => {
                      const rStatus = RECIPIENT_STATUS[r.status] || RECIPIENT_STATUS.queued;
                      const Icon = rStatus.icon;
                      return (
                        <tr key={r.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900">{r.name}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{r.email}</td>
                          <td className="px-4 py-3 text-sm text-gray-500 capitalize">{r.role}</td>
                          <td className="px-4 py-3">
                            <div className={`flex items-center space-x-1.5 text-sm ${rStatus.color}`}>
                              <Icon className="w-3.5 h-3.5" />
                              <span>{rStatus.label}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center text-sm text-gray-500">{r.attempts}</td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {r.sent_at ? formatDateTime(r.sent_at) : '—'}
                          </td>
                          <td className="px-4 py-3 text-sm text-red-500 max-w-[200px] truncate" title={r.error_message}>
                            {r.error_message || '—'}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
                <p className="text-sm text-gray-600">
                  {(pagination.page - 1) * pagination.limit + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                </p>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => loadRecipients(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                    className="p-1.5 rounded border border-gray-300 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-sm text-gray-600">Page {pagination.page}/{pagination.totalPages}</span>
                  <button
                    onClick={() => loadRecipients(pagination.page + 1)}
                    disabled={pagination.page >= pagination.totalPages}
                    className="p-1.5 rounded border border-gray-300 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'preview' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center space-x-2">
              <Mail className="w-4 h-4 text-gray-400" />
              <span className="text-sm font-medium text-gray-700">Subject:</span>
              <span className="text-sm text-gray-900">{campaign.subject}</span>
            </div>
          </div>
          <iframe
            title="Campaign Email Preview"
            srcDoc={campaign.body_html}
            className="w-full h-[600px] border-0"
            sandbox=""
          />
        </div>
      )}
    </div>
  );
};

// ─── Helper Components ────────────────────────────────────────────────────────

function StatCard({ label, value, color }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      <p className="text-xs text-gray-500 mt-1">{label}</p>
    </div>
  );
}

function InfoItem({ label, value }) {
  return (
    <div>
      <p className="text-xs text-gray-500 mb-0.5">{label}</p>
      <p className="text-sm font-medium text-gray-900">{value}</p>
    </div>
  );
}

/**
 * Format MySQL datetime string for display in IST.
 * MySQL stores local time (IST) without timezone indicator.
 * We append +05:30 so JS Date interprets it correctly.
 */
function formatDateTime(dateStr) {
  if (!dateStr) return '—';
  // MySQL format: "2026-08-26 12:52:55" or "2026-08-26T12:52:55"
  // Append IST offset so JS doesn't interpret as UTC
  const normalized = dateStr.replace(' ', 'T');
  const withTZ = normalized.includes('+') || normalized.includes('Z') ? normalized : normalized + '+05:30';
  return new Date(withTZ).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
}

export default CampaignDetail;
